import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ProductConflictDTO, PublishProductFromTemplateDTO, ConflictResolutionOption } from '../../../../base/models/models.swagger.generated';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';

@Component({
  selector: 'app-conflict-detection',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ButtonModule],
  templateUrl: './conflict-detection.component.html',
  styleUrls: ['./conflict-detection.component.scss']
})
export class ConflictDetectionComponent {
  @Input() conflicts: ProductConflictDTO[] = [];
  @Input() selectedTemplateIds: number[] = [];
  @Input() templateOverrides: Map<number, { price: number; quantity: number }> = new Map();
  @Input() sellerProfileId: number | null = null;
  @Output() conflictsResolved = new EventEmitter<PublishProductFromTemplateDTO[]>();

  public ConflictResolutionOption = ConflictResolutionOption;
  public conflictResolutions: Map<number, ConflictResolutionOption> = new Map();

  constructor() { }

  ngOnInit() {
    this.initializeResolutions();
  }

  private initializeResolutions() {
    this.selectedTemplateIds.forEach(templateId => {
      const conflict = this.getConflict(templateId);
      if (conflict?.hasConflict) {
        this.conflictResolutions.set(templateId, ConflictResolutionOption.UpdateExisting);
      } else {
        this.conflictResolutions.set(templateId, ConflictResolutionOption.CreateNew);
      }
    });
  }

  public setResolution(templateId: number, resolution: ConflictResolutionOption) {
    this.conflictResolutions.set(templateId, resolution);
  }

  public getResolution(templateId: number): ConflictResolutionOption {
    return this.conflictResolutions.get(templateId) || ConflictResolutionOption.CreateNew;
  }

  public hasConflict(templateId: number): boolean {
    return this.conflicts.some(c => c.templateId === templateId && c.hasConflict);
  }

  public getConflict(templateId: number): ProductConflictDTO | undefined {
    return this.conflicts.find(c => c.templateId === templateId);
  }

  public canProceed(): boolean {
    return this.sellerProfileId !== null && this.sellerProfileId !== undefined;
  }

  public proceed() {
    if (!this.canProceed()) {
      return;
    }

    const requests: PublishProductFromTemplateDTO[] = this.selectedTemplateIds.map(templateId => {
      const resolution = this.getResolution(templateId);
      const conflict = this.getConflict(templateId);
      const overrides = this.templateOverrides?.get(templateId);
      
      return new PublishProductFromTemplateDTO({
        templateId,
        resolutionOption: resolution,
        existingProductIdToUpdate: resolution === ConflictResolutionOption.UpdateExisting ? conflict?.existingProductId : undefined,
        overridePrice: overrides?.price,
        overrideQuantity: overrides?.quantity,
        sellerProfileId: this.sellerProfileId || undefined
      });
    });

    this.conflictsResolved.emit(requests);
  }
}
